import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Box, Typography, Grid, CircularProgress, Alert, Tooltip, IconButton
} from '@mui/material';
import {
  Refresh, Schedule, Storage as StorageIcon, Image as ImageIcon,
  Face as FaceIcon, PhotoAlbum as PhotoAlbumIcon, CloudSync as CloudSyncIcon,
  Backup as BackupIcon, CheckCircle, Cancel
} from '@mui/icons-material';
import { GlassmorphicCard, SecondaryText, BodyText, SectionTitle, FlexBox, fadeInUp } from '../styles/sharedStyles';

const ImmichNightlyTasks = ({ apiBaseUrl, token }) => {
  const [tasks, setTasks] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchTasks = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      console.log('--- DEBUG: Fetching Immich Config via Backend Proxy ---');
      const proxyUrl = `${apiBaseUrl}/api/immich/system-config`;
      console.log('Proxy URL:', proxyUrl);
      
      const response = await axios.get(proxyUrl, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json'
        },
        timeout: 15000
      });

      console.log('--- DEBUG: Proxy Response Received ---');
      if (response.data && response.data.nightlyTasks) {
        setTasks(response.data.nightlyTasks);
      } else {
        setError("Bracie, nie znalazłem sekcji 'nightlyTasks'. Sprawdź wersję API.");
      }
    } catch (err) {
      console.error('--- DEBUG: Proxy Fetch Error ---');
      if (err.response) {
        console.error('Response Data:', err.response.data);
        console.error('Response Status:', err.response.status);
      } else if (err.request) {
        console.error('No response received from proxy. Check backend connectivity.');
      } else {
        console.error('Error:', err.message);
      }
      setError(`Coś nie pykło (Proxy): ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, [apiBaseUrl, token]);

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', py: 10, gap: 2 }}>
        <CircularProgress sx={{ color: '#00d4ff' }} />
        <SecondaryText>Pobieranie konfiguracji przez proxy...</SecondaryText>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert 
          severity="error" 
          sx={{ 
            borderRadius: '16px', 
            background: 'rgba(255, 68, 68, 0.1)',
            color: '#ff4444',
            border: '1px solid rgba(255, 68, 68, 0.2)',
            '& .MuiAlert-icon': { color: '#ff4444' }
          }}
          action={
            <IconButton color="inherit" size="small" onClick={fetchTasks}>
              <Refresh />
            </IconButton>
          }
        >
          {error}
        </Alert>
        <Box sx={{ mt: 2, p: 2, borderRadius: '8px', background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.1)' }}>
          <SecondaryText sx={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.5)' }}>
            Debug Tip: Check browser console and backend logs for CORS or connection errors.
          </SecondaryText>
        </Box>
      </Box>
    );
  }

  const taskList = [
    { key: 'clusterNewFaces', label: 'Cluster New Faces', icon: <FaceIcon />, color: '#9c27b0' },
    { key: 'databaseCleanup', label: 'Database Cleanup', icon: <StorageIcon />, color: '#00d4ff' },
    { key: 'generateMemories', label: 'Generate Memories', icon: <PhotoAlbumIcon />, color: '#e91e63' },
    { key: 'missingThumbnails', label: 'Missing Thumbnails', icon: <ImageIcon />, color: '#ff9800' },
    { key: 'syncQuotaUsage', label: 'Sync Quota Usage', icon: <CloudSyncIcon />, color: '#4caf50' },
  ];

  return (
    <Box sx={{ animation: `${fadeInUp} 0.5s ease-out` }}>
      <FlexBox sx={{ justifyContent: 'space-between', mb: 4, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <SectionTitle sx={{ mb: 0 }}>
            <Schedule sx={{ color: '#00d4ff' }} />
            USTAWIENIA NIGHTLY TASKS
          </SectionTitle>
          <SecondaryText sx={{ ml: 4.5 }}>Zadania nocne konfiguracji systemu Immich</SecondaryText>
        </Box>
        <Tooltip title="Odśwież dane">
          <IconButton 
            onClick={fetchTasks} 
            sx={{ 
              color: '#00d4ff', 
              background: 'rgba(0, 212, 255, 0.1)',
              '&:hover': { background: 'rgba(0, 212, 255, 0.2)' }
            }}
          >
            <Refresh />
          </IconButton>
        </Tooltip>
      </FlexBox>

      <Grid container spacing={3}>
        {/* Start Time Card */}
        <Grid item xs={12} md={4}>
          <GlassmorphicCard sx={{ 
            p: 4, 
            height: '100%', 
            display: 'flex', 
            flexDirection: 'column', 
            justifyContent: 'center', 
            alignItems: 'center', 
            textAlign: 'center',
            background: (theme) => `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.primary.main}0d 100%)`
          }}>
            <Schedule sx={{ fontSize: 56, color: '#00d4ff', mb: 2, filter: 'drop-shadow(0 0 10px rgba(0, 212, 255, 0.4))' }} />
            <Typography variant="h3" sx={{ color: '#ffffff', fontWeight: 800, mb: 1, letterSpacing: '-1px' }}>
              {tasks?.startTime || '--:--'}
            </Typography>
            <Typography variant="overline" sx={{ color: '#00d4ff', fontWeight: 600, letterSpacing: '2px' }}>
              PLANOWANY START
            </Typography>
          </GlassmorphicCard>
        </Grid>

        {/* Database Backup (Hardcoded Info) */}
        <Grid item xs={12} md={8}>
          <GlassmorphicCard sx={{ 
            p: 4, 
            height: '100%', 
            border: (theme) => `1px solid ${theme.palette.success.main}4d`,
            position: 'relative',
            overflow: 'hidden',
            '&::after': {
              content: '""',
              position: 'absolute',
              top: 0,
              right: 0,
              width: '100px',
              height: '100px',
              background: (theme) => `radial-gradient(circle, ${theme.palette.success.main}1a 0%, transparent 70%)`,
              zIndex: 0
            }
          }}>
            <FlexBox sx={{ mb: 2, position: 'relative', zIndex: 1 }}>
              <BackupIcon sx={{ color: '#00ff88', fontSize: 36 }} />
              <Typography variant="h5" sx={{ color: '#ffffff', fontWeight: 700 }}>
                Database Backup (Dump)
              </Typography>
            </FlexBox>
            <BodyText sx={{ mb: 3, position: 'relative', zIndex: 1, opacity: 0.9 }}>
              Baza danych Immich jest automatycznie zrzucana (dump) i archiwizowana każdego dnia, aby zapewnić bezpieczeństwo Twoich metadanych.
            </BodyText>
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5, position: 'relative', zIndex: 1 }}>
               <Typography variant="h4" sx={{ color: '#00ff88', fontWeight: 800 }}>
                 Codziennie o 23:45
               </Typography>
              <SecondaryText sx={{ color: 'rgba(0, 255, 136, 0.6)', fontWeight: 600 }}>
                HARDCODED
              </SecondaryText>
            </Box>
          </GlassmorphicCard>
        </Grid>

        {/* Task Details Header */}
        <Grid item xs={12} sx={{ mt: 2 }}>
          <Typography variant="h6" sx={{ color: '#ffffff', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{ width: 4, height: 24, bgcolor: '#00d4ff', borderRadius: 1 }} />
            Status Zadań
          </Typography>
        </Grid>

        {/* Task Details List */}
        {taskList.map((task, index) => (
          <Grid item xs={12} sm={6} md={4} key={task.key}>
            <GlassmorphicCard sx={{ 
              p: 2.5, 
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)', 
              '&:hover': { 
                transform: 'translateY(-6px)',
                background: 'rgba(255, 255, 255, 0.05)',
                borderColor: 'rgba(255, 255, 255, 0.3)'
              },
              borderRadius: (theme) => `${(theme.custom?.cardRounding || 28) * 0.6}px`,
              animation: `${fadeInUp} 0.5s ease-out`,
              animationDelay: `${0.1 + index * 0.05}s`,
              animationFillMode: 'both'
            }}>
              <FlexBox sx={{ justifyContent: 'space-between' }}>
                <FlexBox>
                  <Box sx={{ 
                    p: 1.5, 
                    borderRadius: '16px', 
                    background: `${task.color}15`, 
                    color: task.color,
                    display: 'flex',
                    border: `1px solid ${task.color}30`
                  }}>
                    {task.icon}
                  </Box>
                  <Box>
                    <Typography sx={{ color: '#ffffff', fontWeight: 600, fontSize: '0.95rem' }}>
                      {task.label}
                    </Typography>
                    <SecondaryText sx={{ fontSize: '0.75rem' }}>Automatyczne</SecondaryText>
                  </Box>
                </FlexBox>
                {tasks?.[task.key] ? (
                  <Tooltip title="Włączone">
                    <CheckCircle sx={{ color: '#00ff88', filter: 'drop-shadow(0 0 5px rgba(0, 255, 136, 0.4))' }} />
                  </Tooltip>
                ) : (
                  <Tooltip title="Wyłączone">
                    <Cancel sx={{ color: '#ff4444', filter: 'drop-shadow(0 0 5px rgba(255, 68, 68, 0.4))' }} />
                  </Tooltip>
                )}
              </FlexBox>
            </GlassmorphicCard>
          </Grid>
        ))}
      </Grid>

      <Box sx={{ 
        mt: 6, 
        p: 3, 
        borderRadius: '20px', 
        background: 'rgba(255, 255, 255, 0.02)', 
        border: '1px solid rgba(255, 255, 255, 0.05)',
        textAlign: 'center'
      }}>
        <SecondaryText sx={{ fontStyle: 'italic' }}>
          Dane pobierane przez proxy backendu z API Immich (im.raidnet.pl).
        </SecondaryText>
      </Box>
    </Box>
  );
};

export default ImmichNightlyTasks;
